// @ts-nocheck -- Transitional typing for a legacy JavaScript module.
import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import NewDealFooter from "@/components/deals/new-deal/NewDealFooter";
import NewDealSidebar from "@/components/deals/new-deal/NewDealSidebar";
import NewDealSteps from "@/components/deals/new-deal/NewDealSteps";
import { clientNamesNeedReview } from "@/utils/deals.utils";
import { NEW_DEAL_STEPS } from "@/constant/deals.constant";
import { cn } from "@/utils/common.utils";

export default function NewDealDialog({ open, onOpenChange, form, setForm, onSubmit, busy = false }) {
  const [step, setStep] = useState(1);

  useEffect(() => {
    if (!open) setStep(1);
  }, [open]);

  const fullAddress = useMemo(() => [
    form.address,
    form.city,
    [form.state, form.zip_code].filter(Boolean).join(" "),
  ].filter(Boolean).join(", "), [form.address, form.city, form.state, form.zip_code]);

  // Phase follows the side, unless the agent flagged the deal as already under
  // contract on step 1 — that choice outranks the pathway default.
  const updateSide = (dealType) => {
    if (!dealType) return;
    setForm((current) => ({
      ...current,
      deal_type: dealType,
      phase: current.under_contract
        ? "under_contract"
        : dealType === "buy" ? "pre_approval" : "pre_listing",
    }));
  };

  const goBack = () => {
    if (step === 1) onOpenChange(false);
    else setStep((current) => current - 1);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="left-0 top-0 grid h-dvh w-full max-w-none translate-x-0 translate-y-0 grid-cols-1 gap-0 overflow-hidden rounded-none border-0 bg-white p-0 font-dashboard shadow-2xl sm:left-1/2 sm:top-1/2 sm:h-[calc(100dvh-2rem)] sm:w-[calc(100vw-2rem)] sm:max-w-[900px] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-[24px] md:h-[min(636px,calc(100dvh-2rem))] md:grid-cols-[280px_minmax(0,1fr)]"
        data-testid="new-deal-modal"
      >
        <DialogTitle className="sr-only">Open a new deal</DialogTitle>
        <DialogDescription className="sr-only">Add the property, choose the transaction side, and review the new deal.</DialogDescription>

        <NewDealSidebar step={step} />

        <section className="flex h-full min-h-0 min-w-0 flex-col bg-white">
          <div className="flex shrink-0 items-center gap-1 pl-4 pr-16 pt-[max(1rem,env(safe-area-inset-top))] sm:pl-6 md:hidden" aria-label={`Step ${step} of 3`}>
            {NEW_DEAL_STEPS.map((item, index) => (
              <span key={item.title} className={cn("h-1 flex-1 rounded-full", index < step ? "bg-dash-greenDeep" : "bg-dash-border")} />
            ))}
          </div>

          <NewDealSteps
            step={step}
            form={form}
            setForm={setForm}
            fullAddress={fullAddress}
            onAdvance={() => setStep((current) => Math.min(3, current + 1))}
            onSideChange={updateSide}
            onSubmit={onSubmit}
            onEditStep={setStep}
          />

          <NewDealFooter
            step={step}
            onBack={goBack}
            onContinue={() => setStep((current) => current + 1)}
            busy={busy}
            blockedReason={step === 3 && clientNamesNeedReview(form)
              ? `Confirm the ${form.deal_type === "buy" ? "buyer" : "seller"} name to continue.`
              : undefined}
          />
        </section>
      </DialogContent>
    </Dialog>
  );
}
